import { c as _c } from "react/compiler-runtime";
import type { AtlasFile } from "@-/prisma";
import { DisplayEmptyState } from "@/components/display/_internal/display-recipes";
import { ImageDocumentPreview } from "@/components/display/ImageDocumentPreview";
import { HeicImage } from "@/components/HeicImage";
import { getApplicationFilePresentationUrl } from "@/utils/application-file-presentation";
import { isHeicImageSource } from "@/utils/heic-image";
import { resolveFileTypeKind } from "./FileTypeIcon";
export type FileDisplayFile = Pick<AtlasFile, "downloadUrl" | "mimetype" | "name" | "type" | "url"> & Partial<Pick<AtlasFile, "id">>;
const videoFileExtensionPattern = /\.(?:m4v|mov|mp4|ogg|ogv|webm)(?:$|[?#])/i;
function getUrl(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}
function getYoutubeVideoId(url: string): string | null {
  const parsedUrl = getUrl(url);
  if (!parsedUrl) {
    return null;
  }
  const hostname = parsedUrl.hostname.replace(/^www\./, "");
  if (hostname === "youtube.com" || hostname === "m.youtube.com") {
    if (parsedUrl.pathname === "/watch") {
      return parsedUrl.searchParams.get("v");
    }
    if (parsedUrl.pathname.startsWith("/embed/") || parsedUrl.pathname.startsWith("/shorts/")) {
      return parsedUrl.pathname.split("/").filter(Boolean)[1] ?? null;
    }
  }
  if (hostname === "youtube-nocookie.com" && parsedUrl.pathname.startsWith("/embed/")) {
    return parsedUrl.pathname.split("/").filter(Boolean)[1] ?? null;
  }
  if (hostname === "youtu.be") {
    return parsedUrl.pathname.split("/").filter(Boolean)[0] ?? null;
  }
  return null;
}
function getYoutubeEmbedUrl(url: string): string | null {
  const videoId = getYoutubeVideoId(url);
  if (!videoId) {
    return null;
  }
  const embedUrl = new URL(`https://www.youtube.com/embed/${encodeURIComponent(videoId)}`);
  embedUrl.searchParams.set("autoplay", "0");
  embedUrl.searchParams.set("rel", "0");
  return embedUrl.toString();
}
function getVimeoVideoId(url: string): string | null {
  const parsedUrl = getUrl(url);
  if (!parsedUrl) {
    return null;
  }
  const hostname = parsedUrl.hostname.replace(/^www\./, "");
  const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);
  if (hostname === "player.vimeo.com" && pathSegments[0] === "video") {
    return pathSegments[1] ?? null;
  }
  if (hostname === "vimeo.com") {
    return pathSegments.find(segment => /^\d+$/.test(segment)) ?? null;
  }
  return null;
}
function getVimeoEmbedUrl(url: string): string | null {
  const videoId = getVimeoVideoId(url);
  if (!videoId) {
    return null;
  }
  const embedUrl = new URL(`https://player.vimeo.com/video/${encodeURIComponent(videoId)}`);
  embedUrl.searchParams.set("autoplay", "0");
  return embedUrl.toString();
}
function getProviderEmbedUrl(url: string): string | null {
  return getYoutubeEmbedUrl(url) ?? getVimeoEmbedUrl(url);
}
function isVideoFile(file: FileDisplayFile | null | undefined) {
  return Boolean(file?.type.startsWith("video/") || file?.mimetype?.startsWith("video/"));
}
function isLikelyVideoUrl(url: string) {
  return videoFileExtensionPattern.test(url);
}
export function FileDisplay(t0) {
  const $ = _c(20);
  const {
    file,
    url
  } = t0;
  if (!file && !url) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DisplayEmptyState>N/A</DisplayEmptyState>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const sourceUrl = url ?? file?.url;
  if (!sourceUrl) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DisplayEmptyState>N/A</DisplayEmptyState>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== sourceUrl) {
    t1 = getProviderEmbedUrl(sourceUrl);
    $[2] = sourceUrl;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const providerEmbedUrl = t1;
  const embeddableUrl = providerEmbedUrl ?? sourceUrl;
  const shouldRenderVideo = !providerEmbedUrl && (isVideoFile(file) || isLikelyVideoUrl(sourceUrl));
  if (isHeicImageSource(sourceUrl) || /^image\/(?:heic|heif)(?:-sequence)?$/iu.test(file?.mimetype ?? file?.type ?? "")) {
    const t2 = file?.name ?? "Uploaded image";
    let t3;
    if ($[4] !== sourceUrl || $[5] !== t2) {
      t3 = <div className="relative h-full w-full"><HeicImage src={sourceUrl} alt={t2} fill={true} className="object-contain" /></div>;
      $[4] = sourceUrl;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  if (shouldRenderVideo) {
    const t2 = file?.name ?? "Video";
    let t3;
    if ($[7] !== sourceUrl || $[8] !== t2) {
      t3 = <video src={sourceUrl} className="h-full w-full object-contain" title={t2} controls={true} playsInline={true} preload="metadata" />;
      $[7] = sourceUrl;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  if (!providerEmbedUrl && resolveFileTypeKind({
    name: file?.name ?? sourceUrl,
    type: file?.type,
    mimetype: file?.mimetype
  }) === "image") {
    let t2;
    if ($[10] !== file || $[11] !== sourceUrl || $[12] !== url) {
      t2 = url ?? getApplicationFilePresentationUrl(file) ?? sourceUrl;
      $[10] = file;
      $[11] = sourceUrl;
      $[12] = url;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    const imageUrl = t2;
    const t3 = file?.name ?? "Uploaded image";
    let t4;
    if ($[14] !== imageUrl || $[15] !== t3) {
      t4 = <ImageDocumentPreview key={imageUrl} src={imageUrl} title={t3} />;
      $[14] = imageUrl;
      $[15] = t3;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return t4;
  }
  const t2 = file?.name ?? "Video";
  let t3;
  if ($[17] !== embeddableUrl || $[18] !== t2) {
    t3 = <iframe src={embeddableUrl} className="h-full w-full" title={t2} allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen={true} />;
    $[17] = embeddableUrl;
    $[18] = t2;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  return t3;
}